import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./Tabs";
import { Button } from "./Button";
import { Info, X, Camera } from "lucide-react";
import { GoDotFill } from "react-icons/go";
import { TiFlash } from "react-icons/ti";
import { FaRunning } from "react-icons/fa";
import { IoIosFlash } from "react-icons/io";
import { FaPersonRunning } from "react-icons/fa6";

interface CameraSettingsOverlayProps {
    onClose: () => void;
}

const CameraSettingsOverlay: React.FC<CameraSettingsOverlayProps> = ({
    onClose,
}) => {
    const isAndroid =
        typeof navigator !== "undefined" &&
        /Android/i.test(navigator.userAgent);
    const isIOS =
        typeof navigator !== "undefined" &&
        /iPhone|iPad|iPod/i.test(navigator.userAgent);

    const availableTabs: ("android" | "ios")[] = isAndroid
        ? ["android"]
        : isIOS
        ? ["ios"]
        : ["android", "ios"];

    const [tabValue, setTabValue] = useState<"android" | "ios">(
        availableTabs.includes("ios") ? "ios" : "android"
    );

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 sm:p-8">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[75vh] sm:max-h-[80vh] overflow-auto">
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold flex items-center gap-2 text-primary">
                        <Camera className="h-5 w-5" />
                        Camera Settings Guide
                    </h2>
                    <button className="text-gray-500 hover:text-red-500" onClick={onClose}>
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 flex items-start gap-3">
                        <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800">
                            For best results, please set your camera to{" "}
                            <strong>
                                {tabValue === "android" && "F"}HD resolution
                            </strong>{" "}
                            with <strong>30 FPS</strong> (acceptable range:
                            23-60 FPS).
                        </p>
                    </div>

                    <Tabs
                        value={tabValue}
                        onValueChange={(value: string) =>
                            setTabValue(value as "android" | "ios")
                        }
                    >
                        {availableTabs.length > 1 && (
                            <TabsList className="grid grid-cols-2 mb-4">
                                <TabsTrigger value="android">
                                    Android
                                </TabsTrigger>
                                <TabsTrigger value="ios">iOS</TabsTrigger>
                            </TabsList>
                        )}
                        <TabsContent value="android" className="space-y-4">
                            <div className="space-y-2">
                                <h3 className="font-semibold text-primary">
                                    Android Camera Settings
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Tap on the resolution overlay to change
                                    settings:
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-gray-100 p-2 text-sm font-medium">
                                        Step 1: Set Your Video Quality to FHD
                                        and 30 FPS
                                    </div>
                                    <div className="p-3 flex justify-center w-full">
                                        <div className="relative w-full">
                                            <div className="bg-black h-[40px] rounded-md flex flex-row items-center justify-end px-2 gap-1 text-gray-300">
                                                <div className="px-1">
                                                    <TiFlash className="w-4 h-4" />
                                                </div>
                                                <div className="px-1">
                                                    <FaRunning className="w-4 h-4" />
                                                </div>
                                                <div className="relative inline-block">
                                                    <div className="flex flex-col items-center justify-center px-2 text-[0.5rem] leading-none space-y-0">
                                                        <span className="leading-none">
                                                            FHD
                                                        </span>
                                                        <span className="leading-none">
                                                            30
                                                        </span>
                                                    </div>
                                                    <div className="absolute inset-0 border-2 border-green-500 rounded-full pointer-events-none py-3 px-2 -top-1.5" />
                                                </div>
                                                <span className="text-[0.5rem]  text-gray-400 align-middle">
                                                    <GoDotFill />
                                                </span>
                                                <div className="rounded-full w-fit border border-gray-400 flex items-center justify-center py-[0.2rem] px-[0.3rem]">
                                                    <span className="text-[0.5rem]  text-gray-400 align-middle">
                                                        HDR
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="my-4 mx-4">
                                                <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-r from-black/50 via-black/30 to-black/55 backdrop-blur-md shadow-lg">
                                                    <div className="relative p-4 text-white grid grid-cols-5 grid-rows-2 gap-2 items-center justify-center">
                                                        <span className="text-sm">
                                                            Size
                                                        </span>
                                                        <span className="text-lg font-semibold">
                                                            8K
                                                        </span>
                                                        <span className="text-lg font-semibold">
                                                            UHD
                                                        </span>
                                                        <div className="relative text-lg font-semibold text-secondary">
                                                            <span>FHD</span>
                                                            <div className="absolute inset-0 border-2 border-green-500 rounded-full pointer-events-none h-11 w-11 -top-[7px] -left-[5px]" />
                                                        </div>
                                                        <span className="text-lg font-semibold">
                                                            HD
                                                        </span>
                                                        <span className="text-sm">
                                                            FPS
                                                        </span>
                                                        <div className="relative text-lg font-semibold text-secondary">
                                                            <span>30</span>
                                                            <div className="absolute inset-0 border-2 border-green-500 rounded-full pointer-events-none h-8 w-8 -top-[2px] -left-[5px]" />
                                                        </div>
                                                        <span className="text-lg font-semibold">
                                                            60
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-3 text-sm">
                                        <ul
                                            className="list-disc pl-5 space-y-1"
                                            style={{
                                                listStyleType: "disc",
                                            }}
                                        >
                                            <li>
                                                Open your phone’s Camera app.
                                            </li>
                                            <li>
                                                Tap on the video resolution
                                                icon—this may look like HD, FHD,
                                                UHD, or 8K at the top or in the
                                                settings menu.
                                            </li>
                                            <li>
                                                Choose FHD at 30 FPS for the
                                                best balance of quality and file
                                                size. For a smaller file size
                                                you can use HD at 30 FPS
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-gray-100 p-2 text-sm font-medium">
                                        Step 2: Confirm Your Settings
                                    </div>
                                    <div className="p-3 flex justify-center w-full">
                                        <div className="relative w-full">
                                            <div className="bg-black h-[40px] rounded-md flex flex-row items-center justify-end px-2 gap-1 text-gray-300 w-full">
                                                <div className="px-1">
                                                    <TiFlash className="w-4 h-4" />
                                                </div>
                                                <div className="px-1">
                                                    <FaRunning className="w-4 h-4" />
                                                </div>
                                                <div className="relative inline-block">
                                                    <div className="relative flex flex-col items-center justify-center px-2 text-[0.5rem] leading-none space-y-0">
                                                        <span className="leading-none">
                                                            FHD
                                                        </span>
                                                        <span className="leading-none">
                                                            30
                                                        </span>
                                                        <div className="absolute inset-0 border-2 border-green-500 rounded-full pointer-events-none h-8 w-8 -top-[8px] -left-[1px]" />
                                                    </div>
                                                </div>
                                                <span className="text-[0.5rem]  text-gray-400 align-middle">
                                                    <GoDotFill />
                                                </span>
                                                <div className="rounded-full w-fit border border-gray-400 flex items-center justify-center py-[0.2rem] px-[0.3rem]">
                                                    <span className="text-[0.5rem]  text-gray-400 align-middle">
                                                        HDR
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-3 text-sm">
                                        <ul
                                            className="list-disc pl-5 space-y-1"
                                            style={{
                                                listStyleType: "disc",
                                            }}
                                        >
                                            <li>
                                                Make sure you see FHD 30 or HD
                                                30 at the top of the camera
                                                screen before recording.
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="text-sm text-gray-600">
                                    <p className="font-medium mb-1">
                                        Additional Tips:
                                    </p>
                                    <ul className="list-disc pl-5 space-y-1">
                                        <li>
                                            On Samsung phones, FHD (1080p) is
                                            the standard setting for
                                            high-quality video. It might vary on
                                            other android devices.
                                        </li>
                                        <li>
                                            Make sure your phone has enough
                                            storage space (a 2-minute video will
                                            use around 100–300 MB).
                                        </li>
                                        <li>
                                            Good lighting makes a big
                                            difference—try to record during the
                                            day or use extra lighting if
                                            indoors.
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="ios" className="space-y-4">
                            <div className="space-y-2">
                                <h3 className="font-semibold text-primary">
                                    iOS Camera Settings
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Tap on the resolution or frame rate to
                                    change settings:
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-gray-100 p-2 text-sm font-medium">
                                        Step 1: Set Your Video Quality to HD and
                                        30 FPS
                                    </div>
                                    <div className="p-3 flex justify-center">
                                        <div className="relative w-full">
                                            <div className="bg-black h-[40px] rounded-md flex flex-row items-center justify-between px-2 gap-1 text-gray-100 w-full">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="relative rounded-full w-fit border border-gray-100 p-[0.2rem] overflow-visible">
                                                        <IoIosFlash className="w-3 h-3" />
                                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                                            <div className="w-[23px] h-[1px] bg-gray-100 rotate-45"></div>
                                                        </div>
                                                    </div>
                                                    <div className="relative rounded-full w-fit border border-gray-100 p-[0.2rem]">
                                                        <FaPersonRunning className="w-3 h-3" />
                                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                                            <div className="w-[23px] h-[1px] bg-gray-100 rotate-45"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-center">
                                                    <span>00:00:00</span>
                                                </div>
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="relative leading-none text-[0.7rem]">
                                                        <span>HD</span>
                                                        <div className="absolute inset-0 border-2 border-green-500 rounded-full pointer-events-none h-6 w-6 -top-[6.5px] -left-[4px]" />
                                                    </div>
                                                    <span className="text-[0.3rem]  text-gray-100 align-middle">
                                                        <GoDotFill />
                                                    </span>
                                                    <div className="leading-none text-[0.7rem]">
                                                        <span>30</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-3 text-sm">
                                        <ul className="list-disc pl-5 space-y-1">
                                            <li>
                                                Open the Camera app on your
                                                iPhone. If your settings match,
                                                proceed to the next step.
                                            </li>
                                            <li>
                                                Tap on the resolution indicator
                                                (e.g., 4K or HD) in the
                                                top-right corner of the screen.
                                            </li>
                                            <li>
                                                Continue tapping to cycle
                                                through the available
                                                resolutions until you select HD.
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-gray-100 p-2 text-sm font-medium">
                                        Step 2: Adjust the Frame Rate
                                    </div>
                                    <div className="p-3 flex justify-center">
                                        <div className="relative w-full">
                                            <div className="bg-black h-[40px] rounded-md flex flex-row items-center justify-between px-2 gap-1 text-gray-100 w-full">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="relative rounded-full w-fit border border-gray-100 p-[0.2rem] overflow-visible">
                                                        <IoIosFlash className="w-3 h-3" />
                                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                                            <div className="w-[23px] h-[1px] bg-gray-100 rotate-45"></div>
                                                        </div>
                                                    </div>
                                                    <div className="relative rounded-full w-fit border border-gray-100 p-[0.2rem]">
                                                        <FaPersonRunning className="w-3 h-3" />
                                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                                            <div className="w-[23px] h-[1px] bg-gray-100 rotate-45"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-center">
                                                    <span>00:00:00</span>
                                                </div>
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="relative leading-none text-[0.7rem]">
                                                        <span>HD</span>
                                                    </div>
                                                    <span className="text-[0.3rem]  text-gray-100 align-middle">
                                                        <GoDotFill />
                                                    </span>
                                                    <div className="relative leading-none text-[0.7rem] ">
                                                        <span>30</span>
                                                        <div className="absolute inset-0 border-2 border-green-500 rounded-full pointer-events-none h-6 w-6 -top-[7px] -left-[5px]" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-3 text-sm">
                                        <p>
                                            If your FPS is set to anything other
                                            than 30, tap on the number in the
                                            top-right corner to cycle through
                                            frame rates until the number becomes{" "}
                                            <strong>30</strong>.
                                        </p>
                                    </div>
                                </div>

                                <div className="border rounded-lg overflow-hidden">
                                    <div className="bg-gray-100 p-2 text-sm font-medium">
                                        Step 3: Confirm Your Settings
                                    </div>
                                    <div className="p-3 flex justify-center">
                                        <div className="relative w-full">
                                            <div className="bg-black h-[40px] rounded-md flex flex-row items-center justify-between px-2 gap-1 text-gray-100 w-full">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="relative rounded-full w-fit border border-gray-100 p-[0.2rem] overflow-visible">
                                                        <IoIosFlash className="w-3 h-3" />
                                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                                            <div className="w-[23px] h-[1px] bg-gray-100 rotate-45"></div>
                                                        </div>
                                                    </div>
                                                    <div className="relative rounded-full w-fit border border-gray-100 p-[0.2rem]">
                                                        <FaPersonRunning className="w-3 h-3" />
                                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                                            <div className="w-[23px] h-[1px] bg-gray-100 rotate-45"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-center">
                                                    <span>00:00:00</span>
                                                </div>
                                                <div className="flex items-center justify-center space-x-2">
                                                    <div className="relative leading-none text-[0.7rem]">
                                                        <span>HD</span>
                                                        <div className="absolute inset-0 border-2 border-green-500 rounded-full pointer-events-none h-6 w-6 -top-[6.5px] -left-[4px]" />
                                                    </div>
                                                    <span className="text-[0.3rem]  text-gray-100 align-middle">
                                                        <GoDotFill />
                                                    </span>
                                                    <div className="relative leading-none text-[0.7rem] ">
                                                        <span>30</span>
                                                        <div className="absolute inset-0 border-2 border-green-500 rounded-full pointer-events-none h-6 w-6 -top-[7px] -left-[5px]" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-3 text-sm">
                                        <ul
                                            className="list-disc pl-5 space-y-1"
                                            style={{
                                                listStyleType: "disc",
                                            }}
                                        >
                                            <li>
                                                Make sure you see HD 30 at the
                                                top of the camera screen before
                                                recording.
                                            </li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="text-sm text-gray-600">
                                    <p className="font-medium mb-1">
                                        Additional Tips:
                                    </p>
                                    <ul className="list-disc pl-5 space-y-1">
                                        <li>
                                            Make sure your phone has enough
                                            storage space (a 2-minute video will
                                            use around 100–300 MB).
                                        </li>
                                        <li>
                                            Good lighting makes a big
                                            difference—try to record during the
                                            day or use extra lighting if
                                            indoors.
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>

                <div className="border-t p-4 flex justify-between">
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                    <Button onClick={onClose}>Got it</Button>
                </div>
            </div>
        </div>
    );
};

export default CameraSettingsOverlay;
